import { useEffect, useState } from 'react';
import { CommunicationTrackingEventOut, CommunicationTrackingEventType, OrderService, VoucherService } from '../api';

export type CommunicationTrackingStatus = {
  processed: boolean;
  delivered: boolean;
  opened: boolean;
  failed: boolean;
  totalEmails: number;
  events: CommunicationTrackingEventOut[];
};

export const useCommunicationTracking = (orderId?: string, voucherId?: string) => {
  const [status, setStatus] = useState<CommunicationTrackingStatus>({
    processed: false,
    delivered: false,
    opened: false,
    failed: false,
    totalEmails: 0,
    events: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId && !voucherId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let events: CommunicationTrackingEventOut[] = [];

        if (orderId) {
          events = await OrderService.getOrderCommunicationsTracking(orderId);
        } else if (voucherId) {
          events = await VoucherService.getVoucherEmailCommunications(voucherId);
        }

        const emailIds = new Set(events.map((e) => e.sendgrid_email_data?.sg_message_id).filter(Boolean));
        const totalEmails = emailIds.size;

        const processed = events.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_PROCESSED);
        const delivered = events.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_DELIVERED);
        const opened = events.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_OPEN);
        const failed = events.some(
          (e) =>
            e.event_type === CommunicationTrackingEventType.EMAIL_BOUNCE ||
            e.event_type === CommunicationTrackingEventType.EMAIL_DROPPED ||
            e.event_type === CommunicationTrackingEventType.EMAIL_SPAM_REPORT,
        );

        const getCommunicationType = (eventType: CommunicationTrackingEventType): string => {
          if (eventType.startsWith('email_')) return 'email';
          if (eventType.startsWith('sms_')) return 'sms';
          if (eventType.startsWith('whatsapp_')) return 'whatsapp';
          return 'other';
        };

        const sortedEvents = events.sort((a, b) => {
          const typeA = getCommunicationType(a.event_type);
          const typeB = getCommunicationType(b.event_type);

          if (typeA !== typeB) {
            return typeA.localeCompare(typeB);
          }

          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });

        setStatus({
          processed,
          delivered,
          opened,
          failed,
          totalEmails,
          events: sortedEvents,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch communication tracking');
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [orderId, voucherId]);

  return { status, loading, error };
};
